import { supabase } from "@/integrations/supabase/client";

export type HerdAnalyticsEvent = 
  | 'herd_joined'
  | 'herd_left'
  | 'herd_join_failed'
  | 'herd_leave_failed';

interface HerdAnalyticsPayload {
  event: HerdAnalyticsEvent;
  userId: string;
  herdId: string;
  herdName?: string;
  herdCategory?: string;
  errorMessage?: string;
  metadata?: Record<string, any>;
}

export const trackHerdEvent = async (payload: HerdAnalyticsPayload) => {
  const { event, userId, herdId, herdName, herdCategory, errorMessage, metadata } = payload;
  
  // Log to console for debugging
  console.log(`[Herd Analytics] ${event}`, {
    userId,
    herdId,
    herdName,
    herdCategory,
    errorMessage,
    metadata,
    timestamp: new Date().toISOString(),
  });

  try {
    await supabase.from('herd_analytics').insert({
      user_id: userId,
      herd_id: herdId,
      event_type: event,
      metadata: {
        herd_name: herdName,
        herd_category: herdCategory,
        error_message: errorMessage,
        ...metadata,
      },
    });
  } catch (error) {
    // Silent fail - analytics shouldn't break the app
    console.error('[Herd Analytics] Failed to store event:', error);
  }
};

// Get herd engagement stats for admin dashboard
export const getHerdEngagementStats = async (herdId?: string) => {
  try {
    let query = supabase
      .from('herd_analytics')
      .select('event_type, created_at, herd_id');
    
    if (herdId) {
      query = query.eq('herd_id', herdId);
    }

    const { data, error } = await query;
    
    if (error) throw error;

    const joins = data?.filter(e => e.event_type === 'herd_joined').length || 0;
    const leaves = data?.filter(e => e.event_type === 'herd_left').length || 0;
    const joinFailures = data?.filter(e => e.event_type === 'herd_join_failed').length || 0;
    const leaveFailures = data?.filter(e => e.event_type === 'herd_leave_failed').length || 0;

    return {
      totalJoins: joins,
      totalLeaves: leaves,
      netGrowth: joins - leaves,
      joinFailures,
      leaveFailures,
      retentionRate: joins > 0 ? ((joins - leaves) / joins * 100).toFixed(1) : '100',
    };
  } catch (error) {
    console.error('[Herd Analytics] Failed to get stats:', error);
    return null;
  }
};
